import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Eye,
  FileSignature,
  FileX2,
  Gavel,
  History,
  Layers,
  Pencil,
  Power,
  PowerOff,
  UserMinus,
  UserPlus,
} from "lucide-react";
import apiClient from "@/api/client";

/**
 * Oś czasu jednej firmy — kiedy dodana, kiedy zmiana danych, zakresu i kont.
 *
 * Świadomie bez zamówień i POCK-ów: te mają własne sekcje niżej na tej samej stronie,
 * a wrzucenie ich tutaj zamieniłoby historię karty w drugi dziennik zleceń.
 */

interface HistoryEntry {
  id: string;
  verb: string;
  description: string;
  timestamp: string;
  actor_name: string | null;
  metadata: Record<string, unknown>;
}

const VERBS: Record<string, { label: string; Icon: typeof History; color: string }> = {
  CONTRACTOR_CREATED: {
    label: "Dodanie firmy",
    Icon: Building2,
    color: "text-green-600 dark:text-green-400",
  },
  CONTRACTOR_UPDATED: {
    label: "Zmiana danych",
    Icon: Pencil,
    color: "text-[var(--color-text-muted)]",
  },
  // Firm się nie usuwa, tylko wyłącza — CEO albo dyrektor techniczny.
  CONTRACTOR_DEACTIVATED: {
    label: "Dezaktywacja firmy",
    Icon: PowerOff,
    color: "text-red-600 dark:text-red-400",
  },
  CONTRACTOR_ACTIVATED: {
    label: "Ponowna aktywacja firmy",
    Icon: Power,
    color: "text-green-600 dark:text-green-400",
  },
  CONTRACTOR_SCOPE_CHANGED: {
    label: "Zmiana zakresu współpracy",
    Icon: Layers,
    color: "text-[var(--color-primary)]",
  },
  CONTRACTOR_USER_CREATED: {
    label: "Nowe konto użytkownika",
    Icon: UserPlus,
    color: "text-blue-600 dark:text-blue-400",
  },
  CONTRACTOR_USER_DELETED: {
    label: "Usunięcie konta",
    Icon: UserMinus,
    color: "text-red-600 dark:text-red-400",
  },
  CONTRACTOR_USER_RATES_GRANT_CHANGED: {
    label: "Widoczność stawek",
    Icon: Eye,
    color: "text-amber-600 dark:text-amber-500",
  },
  CONTRACTOR_USER_RFQ_GRANT_CHANGED: {
    label: "Postępowania ofertowe",
    Icon: Gavel,
    color: "text-indigo-600 dark:text-indigo-400",
  },
  CONTRACTOR_USER_LED_PROJECTS_GRANT_CHANGED: {
    label: "Prowadzone projekty",
    Icon: ClipboardList,
    color: "text-teal-600 dark:text-teal-400",
  },
  // Umowy mają własną oś czasu na swojej karcie; tutaj widać tylko założenie
  // i usunięcie — po skasowaniu nie ma już karty, na której ślad mógłby zostać.
  CONTRACTOR_AGREEMENT_CREATED: {
    label: "Nowa umowa ramowa",
    Icon: FileSignature,
    color: "text-green-600 dark:text-green-400",
  },
  CONTRACTOR_AGREEMENT_DELETED: {
    label: "Usunięcie umowy ramowej",
    Icon: FileX2,
    color: "text-red-600 dark:text-red-400",
  },
};

const INITIAL_VISIBLE = 8;

export function ContractorHistorySection({ contractorId }: { contractorId: string }) {
  const [expanded, setExpanded] = useState(false);

  const { data: entries } = useQuery({
    queryKey: ["contractors", contractorId, "history"],
    queryFn: () =>
      apiClient
        .get<{ data: HistoryEntry[] }>(`/contractors/${contractorId}/history/`)
        .then((r) => r.data.data),
  });

  if (!entries) return null;

  const visible = expanded ? entries : entries.slice(0, INITIAL_VISIBLE);

  return (
    <section className="mb-6">
      <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
        <History size={16} className="text-[var(--color-text-muted)]" />
        Historia karty
        <span className="font-normal text-[var(--color-text-muted)]">({entries.length})</span>
      </h2>

      <div className="bg-white p-5 dark:bg-gray-800">
        {entries.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">
            Brak zdarzeń — karta nie była zmieniana, odkąd zbieramy historię.
          </p>
        ) : (
          <>
            <ol className="relative space-y-4 border-l border-[var(--color-border)] pl-5">
              {visible.map((entry) => {
                const config = VERBS[entry.verb];
                const Icon = config?.Icon ?? History;
                return (
                  <li key={entry.id} className="relative">
                    <span
                      className={`absolute -left-[26px] flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white dark:bg-gray-800 ${config?.color ?? ""}`}
                    >
                      <Icon size={12} />
                    </span>
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="text-sm font-medium text-[var(--color-text-primary)]">
                        {config?.label ?? entry.verb}
                      </span>
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {new Date(entry.timestamp).toLocaleString("pl")}
                        {entry.actor_name && ` · ${entry.actor_name}`}
                      </span>
                    </div>
                    {entry.description && (
                      <p className="text-xs text-[var(--color-text-secondary)]">
                        {entry.description}
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>

            {entries.length > INITIAL_VISIBLE && (
              <button
                onClick={() => setExpanded((v) => !v)}
                className="mt-4 inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
              >
                {expanded ? (
                  <>
                    <ChevronDown size={12} /> Pokaż mniej
                  </>
                ) : (
                  <>
                    <ChevronRight size={12} /> Pokaż wszystkie ({entries.length})
                  </>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}
